import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts";
import StatNumber from "./stat-number";
import { ChartTooltip, SERIES } from "./chart-bits";

type Props = {
  label: string;
  data: { totalCount: number; data: any[] } | null;
  /** Index into the fixed categorical order. */
  slot?: number;
};

/**
 * Stat tile with a sparkline: one headline count plus six months of shape.
 *
 * No axes, no gridlines, no legend — a sparkline's job is the shape, and the exact
 * figures live in the headline and the hover readout. A single series needs no
 * legend box; the tile's own label already names what is plotted.
 */
export default function TrendTile({ label, data, slot = 0 }: Props) {
  const color = SERIES[slot % SERIES.length];
  const rows = data?.data ?? [];
  const gradientId = `trend-${label.replace(/\W+/g, "-")}`;

  return (
    <div className="rounded-xl border border-edge-faint bg-overlay p-3.5 flex flex-col transition-colors duration-300 hover:border-edge">
      <p className="text-[11px] font-medium text-light-gray truncate">{label}</p>
      <p className="mt-1 text-[19px] font-bold text-text-primary leading-none">
        <StatNumber value={data?.totalCount ?? 0} unit="chases" />
      </p>

      <div className="mt-2 h-[42px] -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                {/* Area fill is a wash at ~10%, never a saturated block. */}
                <stop offset="0%" stopColor={color} stopOpacity={0.18} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Tooltip
              cursor={false}
              content={({ active, payload }) =>
                active && payload?.length ? (
                  <ChartTooltip
                    title={payload[0]?.payload?.month}
                    rows={[
                      { label, color, value: payload[0]?.value },
                    ]}
                  />
                ) : null
              }
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke={color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill={`url(#${gradientId})`}
              // Dots appear on hover only; a marker on every month is noise at this size.
              dot={false}
              activeDot={{
                r: 4,
                fill: color,
                stroke: "var(--color-surface)",
                strokeWidth: 2,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
